import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

type StreamRender = CustomEvent<{ newStream: Element; render: (stream: Element) => unknown }>;

// Tonight's door log as taps land. The server broadcasts each event as one
// prepend into the hours list: the hour's header, its frame and the row. This
// takes that render over, since only the page knows its filters and which
// hours it already shows. A row the filters exclude only moves the total; a
// row for an hour on the page goes to the top of that hour; a new hour
// arrives whole. The counts the server printed move by one, so the header and
// the subtitle never wait on a request.
export default class extends Controller<HTMLElement> {
  static targets = ["hours", "subtitle", "none"];
  static values = {
    direction: String,
    overridesOnly: Boolean,
    servicesOnly: Boolean,
    query: String,
  };

  declare readonly hoursTarget: HTMLElement;
  declare readonly subtitleTarget: HTMLElement;
  declare readonly noneTarget: HTMLElement;
  declare readonly hasNoneTarget: boolean;
  declare directionValue: string;
  declare overridesOnlyValue: boolean;
  declare servicesOnlyValue: boolean;
  declare queryValue: string;

  private boundIntercept = (event: Event) => this.intercept(event as StreamRender);

  connect() {
    document.addEventListener("turbo:before-stream-render", this.boundIntercept);
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundIntercept);
  }

  private intercept(event: StreamRender) {
    const stream = event.detail.newStream;
    if (stream.getAttribute("target") !== this.hoursTarget.id) return;

    event.detail.render = (element) => {
      const content = element.querySelector("template")?.content;
      if (content) this.arrive(content);
    };
  }

  private arrive(content: DocumentFragment) {
    const frame = content.querySelector<HTMLElement>("turbo-frame");
    const header = frame && content.getElementById(`${frame.id}_header`);
    const row = frame?.firstElementChild as HTMLElement | null;
    if (!frame || !header || !row || document.getElementById(row.id)) return;

    this.bump(this.subtitleTarget, "total", "door_log.subtitle");
    if (!this.matches(row)) return;

    row.classList.add("door-log-arrived");
    const shown = document.getElementById(frame.id);
    if (shown) shown.prepend(row);
    else this.hoursTarget.prepend(header, frame);

    const tally = document.getElementById(`${frame.id}_header`)?.querySelector<HTMLElement>("[data-tally]");
    if (tally) this.bump(tally, row.dataset.direction ?? "in", "door_log.in_out");
    this.bump(this.subtitleTarget, "shown", "door_log.subtitle");
    if (this.hasNoneTarget) this.noneTarget.remove();
  }

  private matches(row: HTMLElement): boolean {
    const data = row.dataset;
    if (this.directionValue && data.direction !== this.directionValue) return false;
    if (this.overridesOnlyValue && data.overridden === undefined) return false;
    if (this.servicesOnlyValue && data.services === undefined) return false;
    return !this.queryValue || (data.search ?? "").includes(this.queryValue.toLowerCase());
  }

  // Moves one count on the element by one and rewrites its copy from all of them.
  private bump(element: HTMLElement, key: string, copy: string) {
    element.dataset[key] = String(Number(element.dataset[key] ?? 0) + 1);
    element.textContent = i18n.t(copy, { ...element.dataset });
  }
}
